// BulkReportExcel.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
} from "@mui/material";
import { Edit, Save, Cancel } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { getToken, base_url } from "../../config/config";

const BulkReportExcel = () => {
  // State Variables
  const [ANR, setANR] = useState("");
  const [cleanANR, setCleanANR] = useState([]);
  const [totalProjectCost, setTotalProjectCost] = useState(0);
  const [totalCostByUser, setTotalCostByUser] = useState(60); // Hourly rate
  const [workData, setWorkData] = useState([]);
  const [userTimes, setUserTimes] = useState({});
  const [excelData, setExcelData] = useState([]);
  const [mergedData, setMergedData] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [invoiceNo, setInvoiceNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch Work Data for ANRs
  const fetchDataForANRs = async (anrList) => {
    try {
      const fetchPromises = anrList.map((anr) =>
        axios.get(`${base_url}/v1/getUserProjectReport/${anr}`),
      );
      const responses = await Promise.all(fetchPromises);

      const allWorkData = responses.flatMap((response) =>
        response.data.status === 1 ? response.data.data : [],
      );

      setWorkData(allWorkData);
    } catch (err) {
      console.error("Error fetching data for ANRs:", err);
      setError("Failed to fetch work data for ANRs.");
    }
  };

  // Handle File Upload and Data Extraction
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        // Clean and Extract ANR Values
        const cleanedAnrValues = jsonData
          .filter((row) => row.Bezeichnung)
          .map((row) => {
            let cleanedBezeichnung = row.Bezeichnung
              ? row.Bezeichnung.replace(/[^\d-]/g, "")
              : "";

            if (cleanedBezeichnung.includes("-")) {
              const parts = cleanedBezeichnung.split("-");
              const lastPart = parts.pop();
              const truncatedLastPart = lastPart.slice(0, 2);
              cleanedBezeichnung = [...parts, truncatedLastPart].join("-");
            }

            return cleanedBezeichnung;
          })
          .filter((anr) => /\d/.test(anr));

        console.log("Cleaned ANR values with numbers:", cleanedAnrValues);
        setCleanANR(cleanedAnrValues);

        // Fetch Work Data for Cleaned ANRs
        fetchDataForANRs(cleanedAnrValues);

        // Extract Excel Data Including Gesamtpreis
        const extractedData = jsonData
          .filter((row) => row.Einzelpreis)
          .map((row) => {
            let cleanedBezeichnung = "";
            let additionalText = "";
            let Menge = parseInt(row.Menge, 10) || 0; // Capture 'Menge' if available
            let Gesamtpreis = parseFloat(row.Gesamtpreis) || 0; // Capture 'Gesamtpreis' if available

            if (row.Bezeichnung) {
              const regex = /Auftrag Nr: \d+-\d+-\d+\s*(.*)/s;
              let match = regex.exec(row.Bezeichnung);

              if (match) {
                additionalText = match[1].trim();
              }

              cleanedBezeichnung = row.Bezeichnung.replace(/[^\d-]/g, "");

              if (cleanedBezeichnung.includes("-")) {
                const parts = cleanedBezeichnung.split("-");
                const lastPart = parts.pop();
                const truncatedLastPart = lastPart.slice(0, 2);
                cleanedBezeichnung = [...parts, truncatedLastPart].join("-");
              }
            }

            return {
              Bezeichnung: cleanedBezeichnung,
              Einzelpreis: parseFloat(row.Einzelpreis),
              AdditionalText: additionalText,
              Menge: Menge, // Ensure 'Menge' is an integer
              Gesamtpreis: Gesamtpreis, // Include 'Gesamtpreis'
            };
          });

        console.log("Extracted Excel Data:", extractedData);
        setExcelData(extractedData);
      } catch (err) {
        console.error("Error processing Excel file:", err);
        setError("Failed to process the uploaded Excel file.");
      }
    };

    reader.readAsBinaryString(file);
  };

  // Merge Excel Data with Work Data and Calculate Additional Fields
  useEffect(() => {
    if (excelData.length > 0 && workData.length > 0) {
      const merged = excelData.map((excelRow) => {
        return {
          ...excelRow,

          Gesamtpreis: excelRow.Gesamtpreis.toFixed(2), // Use provided 'Gesamtpreis'
        };
      });

      setMergedData(merged);
    }
  }, [excelData, workData, userTimes, totalCostByUser]);

  // Calculate User Times based on Work Data
  useEffect(() => {
    if (workData.length > 0) {
      const times = {};

      workData.forEach((entry) => {
        const { user, TotalTime, StartTime, EndTime } = entry;
        let totalHours = 0;

        if (TotalTime && TotalTime !== "00:00:00") {
          const [hours, minutes, seconds] = TotalTime.split(":").map(Number);
          totalHours = hours + minutes / 60 + seconds / 3600;
        } else if (StartTime && EndTime) {
          const start = new Date(`1970-01-01T${StartTime}Z`);
          const end = new Date(`1970-01-01T${EndTime}Z`);
          totalHours = (end - start) / 3600000; // Convert milliseconds to hours
        }

        if (user) {
          if (times[user]) {
            times[user] += totalHours;
          } else {
            times[user] = totalHours;
          }
        }
      });

      setUserTimes(times);
    }
  }, [workData]);

  // Handle Editing a Row
  const handleEditClick = (index) => {
    setEditIndex(index);
    setEditedRow(mergedData[index]);
  };

  // Handle Input Change in Editable Row
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedRow((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle Save Click for Edited Row
  const handleSaveClick = (index) => {
    const { Menge, Gesamtpreis } = editedRow;

    // Validate 'Menge'
    if (
      Menge === "" ||
      isNaN(Menge) ||
      Number(Menge) < 0 ||
      !Number.isInteger(Number(Menge))
    ) {
      alert("Please enter a valid Menge (non-negative integer).");
      return;
    }

    // Validate 'Gesamtpreis'
    if (Gesamtpreis === "" || isNaN(Gesamtpreis) || Number(Gesamtpreis) < 0) {
      alert("Please enter a valid Gesamtpreis (non-negative number).");
      return;
    }

    const updatedData = [...mergedData];
    updatedData[index] = {
      ...editedRow,
      Menge: Number(Menge),
      Gesamtpreis: parseFloat(Gesamtpreis).toFixed(2), // Update 'Gesamtpreis'
    };
    setMergedData(updatedData);
    setEditIndex(null);
  };

  // Handle Cancel Click for Editing
  const handleCancelClick = () => {
    setEditIndex(null);
  };

  // Handle Saving Processed Data to Backend
  const saveProcessedData = async () => {
    try {
      if (!invoiceNo) {
        alert("Please enter the Rechnung Nummer (Invoice Number).");
        return;
      }

      setLoading(true);
      setError(null);

      const response = await axios.post(`${base_url}/v1/saveExcelData`, {
        invoiceNo,
        mergedData,
      });

      if (response.data.status === 1) {
        console.log("Data successfully saved to the database.");
        alert("Data successfully saved to the database.");
        // Optionally, reset state or provide further feedback
      } else {
        console.error("Failed to save data:", response.data.message);
        alert(`Failed to save data: ${response.data.message}`);
      }
    } catch (err) {
      console.error("Error saving processed data:", err);
      setError("Failed to save processed data.");
      alert("An error occurred while saving the data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Typography variant='h4' gutterBottom>
        Bulk Report Excel Upload
      </Typography>

      {/* Input Section */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          flexWrap: "wrap",
          marginBottom: "1rem",
        }}>
        {/* Invoice Number Input */}
        <TextField
          label='Rechnung Nummer'
          type='text'
          value={invoiceNo}
          onChange={(e) => setInvoiceNo(e.target.value)}
          variant='outlined'
          // required
        />

        {/* Excel File Upload Button */}
        <Button variant='contained' color='primary' component='label'>
          Upload Excel
          <input
            type='file'
            accept='.xlsx, .xls'
            onChange={handleFileUpload}
            hidden
          />
        </Button>

        {/* Save Processed Data Button */}
        <Button
          variant='contained'
          color='secondary'
          onClick={saveProcessedData}
          disabled={loading || mergedData.length === 0}>
          {loading ? "Saving..." : "Save Processed Data"}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Typography color='error' variant='body1' gutterBottom>
          {error}
        </Typography>
      )}

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bezeichnung (ANR)</TableCell>
              <TableCell>Einzelpreis (€)</TableCell>
              <TableCell>Additional Text</TableCell>
              <TableCell>Total Hours</TableCell>
              <TableCell>Cost by User (€)</TableCell>
              <TableCell>Profit/Loss (€)</TableCell>
              <TableCell>Menge</TableCell>
              <TableCell>Gesamtpreis (€)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mergedData.map((row, index) => (
              <TableRow key={index}>
                {/* Bezeichnung (ANR) */}
                <TableCell>
                  {editIndex === index ? (
                    <TextField
                      name='Bezeichnung'
                      value={editedRow.Bezeichnung}
                      onChange={handleInputChange}
                      variant='outlined'
                      size='small'
                      disabled // Prevent editing ANR
                    />
                  ) : (
                    row.Bezeichnung
                  )}
                </TableCell>

                {/* Einzelpreis (€) */}
                <TableCell>
                  {editIndex === index ? (
                    <TextField
                      name='Einzelpreis'
                      type='number'
                      value={editedRow.Einzelpreis}
                      onChange={handleInputChange}
                      variant='outlined'
                      size='small'
                      inputProps={{ min: 0, step: "0.01" }}
                      required
                    />
                  ) : (
                    parseFloat(row.Einzelpreis).toFixed(2)
                  )}
                </TableCell>

                {/* Additional Text */}
                <TableCell>
                  {editIndex === index ? (
                    <TextField
                      name='AdditionalText'
                      value={editedRow.AdditionalText}
                      onChange={handleInputChange}
                      variant='outlined'
                      size='small'
                    />
                  ) : (
                    row.AdditionalText
                  )}
                </TableCell>

                {/* Total Hours */}
                <TableCell>{row.totalHours}</TableCell>

                {/* Cost by User (€) */}
                <TableCell>{row.costByUser}</TableCell>

                {/* Profit/Loss (€) */}
                <TableCell style={{ color: row.profitOrLossColor }}>
                  {row.profitOrLoss}
                </TableCell>

                {/* Menge */}
                <TableCell>
                  {editIndex === index ? (
                    <TextField
                      name='Menge'
                      type='number'
                      value={editedRow.Menge}
                      onChange={handleInputChange}
                      variant='outlined'
                      size='small'
                      inputProps={{ min: 0, step: 1 }}
                      error={
                        editedRow.Menge < 0 ||
                        !Number.isInteger(Number(editedRow.Menge))
                      }
                      helperText={
                        editedRow.Menge < 0 ||
                        !Number.isInteger(Number(editedRow.Menge))
                          ? "Enter a valid Menge"
                          : ""
                      }
                      required
                    />
                  ) : (
                    row.Menge
                  )}
                </TableCell>

                {/* Gesamtpreis (€) */}
                <TableCell>
                  {editIndex === index ? (
                    <TextField
                      name='Gesamtpreis'
                      type='number'
                      value={editedRow.Gesamtpreis}
                      onChange={handleInputChange}
                      variant='outlined'
                      size='small'
                      inputProps={{ min: 0, step: "0.01" }}
                      error={
                        editedRow.Gesamtpreis === "" ||
                        isNaN(editedRow.Gesamtpreis) ||
                        Number(editedRow.Gesamtpreis) < 0
                      }
                      helperText={
                        editedRow.Gesamtpreis === "" ||
                        isNaN(editedRow.Gesamtpreis) ||
                        Number(editedRow.Gesamtpreis) < 0
                          ? "Enter a valid Gesamtpreis"
                          : ""
                      }
                      required
                    />
                  ) : (
                    parseFloat(row.Gesamtpreis).toFixed(2)
                  )}
                </TableCell>

                {/* Actions */}
                <TableCell>
                  {editIndex === index ? (
                    <>
                      <IconButton
                        onClick={() => handleSaveClick(index)}
                        color='primary'
                        size='small'>
                        <Save />
                      </IconButton>
                      <IconButton
                        onClick={handleCancelClick}
                        color='secondary'
                        size='small'>
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton
                      onClick={() => handleEditClick(index)}
                      color='primary'
                      size='small'>
                      <Edit />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {/* Display a message if no data is available */}
            {mergedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align='center'>
                  No data available. Please upload an Excel file.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default BulkReportExcel;
